import React from 'react';
import { Popover, Text, Title, useClassnames } from '@wb-tech/ui-react';
import { useTranslation } from 'react-i18next';

import { formatNumber } from '#helper/format/number';
import { PORTAL_LINK } from '#core/const';
import type { IGetApiV5SupplierApiResponse } from '#adapter/api/campaigns/v5/supplier/types';
import type { IModelBalanceResponse } from '#adapter/api/campaigns/v3/balance/types';
import { useCountryCode } from '#hook/use-country-code';
import { useConfigFetch } from '#hook/use-config-fetch';

import { LanguageSelect } from '#component/language-select';
import { CURRENCY } from '#route/finances/components/sum-text';

import style from './index.module.pcss';

interface IUserInfoContent {
    balanceData?: IModelBalanceResponse,
    supplierData?: IGetApiV5SupplierApiResponse,
    cashbackValue?: number
}

export const UserInfoContentV2 = ({ balanceData, supplierData, cashbackValue }: IUserInfoContent) => {
    const cn = useClassnames(style);
    const { t } = useTranslation();
    const { isRussian, isChinese } = useCountryCode(); // TODO: Если ты китаец, то сейчас тебе не нужны баллы )
    const { config: configValuesData } = useConfigFetch();
    const cashbackEnabled = Boolean(configValuesData?.cashbackEnabled);

    const unifiedAccountBalance = formatNumber(balanceData?.content?.unifiedAccount || 0);

    if(!supplierData) {
        return null;
    }

    return (
        <div className={cn('header__dropdown')}>
            <div className={cn('header__dropdown-profile')}>
                <Title tagName="h4">{t('components.header.dropdown.profile')}</Title>
            </div>
            <div className={cn('header__dropdown-content')}>
                <div className={cn('header__dropdown-content-item')}>
                    {supplierData.name}
                    <Text
                        presetColor="secondary"
                        presetSize="caption"
                    >
                        {t('components.header.dropdown.content.company.inn', {
                            value: supplierData.inn
                        })}
                    </Text>
                </div>

                {(isRussian || isChinese) && (
                    <div className={cn('header__dropdown-content-item')}>
                        <Text
                            presetColor="secondary"
                            presetSize="caption"
                        >
                            {t('components.header.dropdown.content.balance-unified.title')}
                        </Text>
                        <div className={cn('header__dropdown-content-item_balance', 'unified')}>
                            <p className={cn('text')}>
                                {t('components.header.dropdown.content.balance-unified.value', {
                                    value: unifiedAccountBalance
                                })}
                                <span className={cn('grey')}>{CURRENCY.rub}</span>
                            </p>
                        </div>
                    </div>
                )}
                {isRussian && cashbackEnabled && (
                    <div className={cn('header__dropdown-content-item')}>
                        <Text
                            presetColor="secondary"
                            presetSize="caption"
                        >
                            {t('components.header.dropdown.content.cashback-unified.title')}
                        </Text>
                        <p className={cn('text')}>
                            {t('components.header.dropdown.content.balance-unified.value', {
                                value: formatNumber(cashbackValue || 0)
                            })}
                            <span className={cn('grey')}>{CURRENCY.balls}</span>
                        </p>
                    </div>
                )}
            </div>
            <div className={cn('header__dropdown-change-company')}>
                <Popover
                    className={{
                        popover__trigger: cn('header__dropdown-change-company-trigger'),
                        popover__content: cn('header__dropdown-change-company-content')
                    }}
                    render={(
                        <Text
                            presetColor="inverse"
                            presetSize="caption"
                        >
                            {t('components.header.dropdown.change-company.popover')}
                        </Text>
                    )}
                    trigger="hover"
                    triggerOffset={4}
                >
                    <a
                        href={PORTAL_LINK}
                        className={cn('header__dropdown-change-company-link')}
                    >
                        <Text>{t('components.header.dropdown.change-company.text')}</Text>
                    </a>
                </Popover>
            </div>

            <div className={cn('header__language-select')}>
                <LanguageSelect />
            </div>

            <div className={cn('header__dropdown-out')}>
                <a
                    href={PORTAL_LINK}
                    className={cn('header__dropdown-out-link')}
                >
                    <Text presetColor="secondary">{t('components.header.dropdown.out')}</Text>
                </a>
            </div>
        </div>
    );
};
